import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import { getGoalAuditLog, GoalAuditLogView } from '../../../../api/PreformanceManagement/Goals';

interface AuditLogViewerProps {
  goalId: string;
  isOpen: boolean;
}

const fieldLabelMap: Record<string, string> = {
  Title: 'Title',
  Description: 'Description',
  Status: 'Status',
  Progress: 'Progress',
  Level: 'Level',
  Owner: 'Owner',
  Department: 'Department',
  ParentId: 'Parent Goal',
  StartDate: 'Start Date',
  DueDate: 'Due Date',
  Priority: 'Priority',
  Blocker: 'Blocker',
};

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({ goalId, isOpen }) => {
  const { t } = useTranslation();
  const [auditLogs, setAuditLogs] = useState<GoalAuditLogView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAuditLog = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getGoalAuditLog(goalId);
      setAuditLogs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to load audit log'));
    } finally {
      setLoading(false);
    }
  }, [goalId, t]);

  useEffect(() => {
    if (isOpen && goalId) {
      loadAuditLog();
    }
  }, [isOpen, goalId, loadAuditLog]);

  const formatTimestamp = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <AppIcon name="Loader2" size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (auditLogs.length === 0) {
    return (
      <div className="text-center py-4">
        <AppIcon name="History" size={32} color="var(--color-muted-foreground)" />
        <p className="text-sm text-muted-foreground mt-1">
          {t('No changes recorded yet')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="audit-log-viewer">
      <div className="space-y-4">
        {auditLogs.map((log, index) => (
          <div
            key={log.id}
            className="relative ps-6 pb-4"
            data-testid={`audit-entry-${index}`}
          >
            {/* Timeline line */}
            {index < auditLogs.length - 1 && (
              <div className="absolute start-2 top-6 bottom-0 w-px bg-border" />
            )}
            {/* Timeline dot */}
            <div className="absolute start-0 top-1 w-4 h-4 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            </div>

            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-foreground">
                {log.changedByName || t('Unknown User')}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatTimestamp(log.changedAt)}
              </span>
            </div>

            {/* Changes */}
            <div className="space-y-1.5">
              {log.changes.map((change, ci) => (
                <div
                  key={ci}
                  className="flex items-start gap-2 text-sm"
                  data-testid={`field-change-${change.field}`}
                >
                  <span className="text-muted-foreground font-medium min-w-[80px]">
                    {t(fieldLabelMap[change.field] || change.field)}:
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {change.oldValue && (
                      <span className="line-through text-destructive/70 bg-destructive/5 px-1.5 py-0.5 rounded text-xs">
                        {change.oldValue}
                      </span>
                    )}
                    <AppIcon name="ArrowRight" size={12} color="var(--color-muted-foreground)" />
                    <span className="text-success bg-success/5 px-1.5 py-0.5 rounded text-xs font-medium">
                      {change.newValue || t('(empty)')}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Comment */}
            {log.comment && (
              <div className="mt-2 flex items-start gap-1.5">
                <AppIcon name="MessageSquare" size={14} color="var(--color-muted-foreground)" className="mt-0.5" />
                <span className="text-xs text-muted-foreground italic">
                  {log.comment}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditLogViewer;
